import type { RecruitingTeamFitElementView, TeamResultView } from "./match-result-codec";
import type { RankSortBy } from "./match-result-derive";

// Eine Achse, eine Stelle. @adr SPARK-0052-recruiting-team-fit-supplements-the-role-ranking@1
// verlangt, dass Bildschirm, PDF und Assistent denselben Team-Beitrag beschreiben; vier Fakten
// darueber standen dafuer in zwei Dateien doppelt — Sortier-Rueckfall, Beitrags-Map, tragende
// Powers und der Ausschluss des Bestandsteams. Die Rueckfall-Basis kommt aus dem Design: zieht ein
// Mitglied die Einwilligung zu seinen Power-Werten zurueck, entfernt genesis den ganzen
// recruitingTeamFit-Block, und ohne den Rueckfall taucht das Bestandsteam wieder als Kandidat auf.
export interface TeamFitBasisLike {
  readonly members: readonly { readonly id: string }[];
}

interface FactSkillLike {
  readonly teamMembers: readonly { readonly personId: string }[];
}

export interface TeamFitAxis {
  /** "team" ohne Basis ist keine Achse: dann sortiert die Liste wieder nach Smycles-Eignung. */
  readonly sortBy: RankSortBy;
  readonly contributionById?: ReadonlyMap<string, number | null>;
  readonly topById?: ReadonlyMap<string, readonly RecruitingTeamFitElementView[]>;
  readonly incumbentIds: ReadonlySet<string>;
  /** Das Bestandsteam bewirbt sich nicht auf die eigene offene Rolle. */
  withoutIncumbents<T extends FactSkillLike>(factSkill: T | null): T | null;
}

export function resolveTeamFit(
  view: TeamResultView,
  requestedSortBy: RankSortBy,
  fallbackBasis?: TeamFitBasisLike | null,
): TeamFitAxis {
  const fit = view.recruitingTeamFit ?? null;
  const incumbentIds = new Set((fit?.basis ?? fallbackBasis)?.members.map(m => m.id) ?? []);
  return {
    sortBy: requestedSortBy === "team" && !fit ? "smycles" : requestedSortBy,
    contributionById: fit ? new Map(fit.candidates.map(c => [c.personId, c.contribution])) : undefined,
    topById: fit ? new Map(fit.candidates.map(c => [c.personId, c.top])) : undefined,
    incumbentIds,
    withoutIncumbents<T extends FactSkillLike>(factSkill: T | null): T | null {
      if (!factSkill || incumbentIds.size === 0) return factSkill;
      return {
        ...factSkill,
        teamMembers: factSkill.teamMembers.filter(member => !incumbentIds.has(member.personId)),
      };
    },
  };
}
